import { Box, List, Skeleton, Stack, Chip, alpha } from "@mui/material";
import { useAppPathname } from "@/routing/navigation";
import DrawerCollapse from "../NavMenu/DrawerCollapse";
import { PlanCollapse } from "./PlanCollapse";
import {
  INTEGRATION_ITEMS,
  PLANS_PRICING_ALTERNATIVES,
  PLANS_PRICING_TARGET,
  SUBSCRIPTION_ITEMS,
} from "./navItems";
import SectionTitle from "./SectionTitle";
import NavListItem from "./NavListItem";
import useNavListStyle from "./hooks/useNavListStyle";
import useNavPlans from "./hooks/useNavPlans";
import { useHasVerifiedProxyPlan } from "@/hooks/useHasVerifiedProxyPlan";
import MenuIcon from "../NavMenu/MenuIcon";
import { useReferralConfig } from "@/components/cards/ReferralCards";
import { Highlighter } from "@/ui/Highlighter";

function IntegrationsSection({
  autoCloseDrawer,
}: {
  autoCloseDrawer: () => void;
}) {
  const pathname = useAppPathname();

  return (
    <DrawerCollapse
      isOpen={
        pathname.startsWith("/integrations") || pathname.startsWith("/userapi")
      }
      title="Integrations"
      titleComponent={
        <Stack
          component="span"
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          pr={2}
        >
          <span>Integrations</span>
          <Highlighter
            severity="info"
            innerVariant="opacity"
            color="info"
            size="small"
          >
            NEW
          </Highlighter>
        </Stack>
      }
      icon={
        <MenuIcon
          icon="Integrations"
          selected={pathname.startsWith("/integrations")}
        />
      }
      items={INTEGRATION_ITEMS}
      autoCloseDrawer={autoCloseDrawer}
    />
  );
}

export default function NavMenuMultiPlan({
  autoCloseDrawer,
}: {
  autoCloseDrawer: () => void;
}) {
  const pathname = useAppPathname();
  const listStyle = useNavListStyle();
  const { planByType, isLoadingPlans } = useNavPlans();
  const hasVerifiedPlan = useHasVerifiedProxyPlan();

  const { data: config } = useReferralConfig();

  const isAffiliate = config?.mode === "payout";

  return (
    <List sx={listStyle}>
      <NavListItem
        title="Dashboard"
        to="/dashboard"
        icon={
          <MenuIcon icon="dashboard" selected={pathname === "/dashboard"} />
        }
        autoCloseDrawer={autoCloseDrawer}
      />
      <SectionTitle title="Products" />
      {isLoadingPlans ? (
        <>
          <Skeleton height={48} />
          <Skeleton height={48} />
          <Skeleton height={48} />
          <Skeleton height={48} />
        </>
      ) : (
        <>
          {!!planByType.free && (
            <PlanCollapse
              plan={planByType.free}
              planKey="free"
              title="Free"
              icon="/free-icon.svg"
              autoCloseDrawer={autoCloseDrawer}
            />
          )}
          <PlanCollapse
            plan={planByType.datacenter}
            planKey="datacenter"
            title="Proxy Server"
            icon="/datacenter-icon.svg"
            autoCloseDrawer={autoCloseDrawer}
          />
          <PlanCollapse
            plan={planByType.staticResidential}
            planKey="staticResidential"
            title="Static Residential"
            icon="/static-residential-icon.svg"
            autoCloseDrawer={autoCloseDrawer}
          />
          <PlanCollapse
            plan={planByType.residential}
            planKey="residential"
            title={"Rotating Residential"}
            icon="/residential-icon.svg"
            autoCloseDrawer={autoCloseDrawer}
          />
          {hasVerifiedPlan && (
            <PlanCollapse
              plan={planByType.verified}
              planKey="verified"
              title="Verified"
              icon="/verified-icon.svg"
              autoCloseDrawer={autoCloseDrawer}
            />
          )}
        </>
      )}
      <Box sx={{ mx: 2, my: 2, borderTop: 1, borderColor: "divider" }} />
      <NavListItem
        title="Plans & Pricing"
        to={PLANS_PRICING_TARGET.to}
        alternativeTo={PLANS_PRICING_ALTERNATIVES.map((target) => target.to)}
        icon={
          <MenuIcon
            icon="pricing"
            selected={pathname.startsWith("/subscription")}
          />
        }
        autoCloseDrawer={autoCloseDrawer}
      />
      <Box sx={{ mx: 2, my: 2, borderTop: 1, borderColor: "divider" }} />
      <NavListItem
        title="Proxy Tester"
        to="/proxy-tester"
        icon={
          <MenuIcon icon="Server" selected={pathname === "/proxy-tester"} />
        }
        badge={
          <Chip
            label="New"
            size="small"
            sx={(theme) => ({
              height: 22,
              color: "info.main",
              bgcolor: alpha(theme.palette.info.main, 0.08),
              textTransform: "uppercase",
              fontSize: 10,
              fontWeight: 600,
            })}
          />
        }
        autoCloseDrawer={autoCloseDrawer}
      />
      <DrawerCollapse
        isOpen={
          pathname === "/subscription" || pathname === "/subscription/invoices"
        }
        title="Subscription"
        autoCloseDrawer={autoCloseDrawer}
        icon={
          <MenuIcon
            icon="Subscription"
            selected={
              pathname === "/subscription" ||
              pathname === "/subscription/invoices"
            }
          />
        }
        items={SUBSCRIPTION_ITEMS}
      />

      <NavListItem
        title={isAffiliate ? "Affiliate" : "Referral"}
        to="/earn/referral"
        icon={
          <MenuIcon icon="Affiliate" selected={pathname === "/earn/referral"} />
        }
        autoCloseDrawer={autoCloseDrawer}
      />
      <IntegrationsSection autoCloseDrawer={autoCloseDrawer} />
    </List>
  );
}
